import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontSize } from "../../component/theme/fontSize";
import { fontWeight } from "../../component/theme/fontWeight";
import { ThemeColor } from "../../component/theme/color";

const matchesName = (name, term) => String(name || "").toLowerCase().includes(term);

/**
 * Name filter for the folder/file lists that are already in state.
 *
 * The API exposes no search endpoint (see Redux/service/FileManagerService.js),
 * so this narrows what is on screen for the folder level currently open instead
 * of searching the whole tree. Filtering is done in memory, which also means it
 * is instant and needs no debounce.
 */
export const useFileManagerSearch = (folders, files) => {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();

  const filteredFolders = useMemo(() => {
    const list = folders || [];
    return term ? list.filter((folder) => matchesName(folder?.name, term)) : list;
  }, [folders, term]);

  const filteredFiles = useMemo(() => {
    const list = files || [];
    return term ? list.filter((file) => matchesName(file?.name, term)) : list;
  }, [files, term]);

  return {
    search,
    setSearch,
    isSearching: term.length > 0,
    filteredFolders,
    filteredFiles,
    resultCount: filteredFolders.length + filteredFiles.length,
  };
};

export const FileManagerSearch = ({ value, onChange, isSearching }) => (
  <TextField
    className="fm-search"
    size="small"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder="Search folders and files"
    aria-label="Search folders and files"
    autoComplete="off"
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon fontSize="small" />
        </InputAdornment>
      ),
      endAdornment: isSearching ? (
        <InputAdornment position="end">
          <Tooltip title="Clear search">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => onChange("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ) : null,
    }}
  />
);

export const FileManagerNoResult = ({ search }) => (
  <Box className="fm-empty-result">
    <FolderOffOutlinedIcon sx={{ fontSize: 40, color: ThemeColor.GREY }} />
    <Typography
      fontFamily={FontFamily.Poppins}
      fontWeight={fontWeight.W600}
      fontSize={fontSize.h4.large}
    >
      No match for "{search}"
    </Typography>
    <Typography
      fontFamily={FontFamily.Poppins}
      fontSize={fontSize.h4.medium}
      color={ThemeColor.GREY}
    >
      Nothing in this folder has that name. Try a shorter search term.
    </Typography>
  </Box>
);
